import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { getClassTeams, getMyTeam, getProfile } from '../../api';
import './Equipes.css';

const RANK_MEDALS = ['🥇', '🥈', '🥉'];

function getRankLabel(index) {
  return RANK_MEDALS[index] ?? `${index + 1}.`;
}

// Cherche classroom_id dans différentes structures possibles de l'objet profil
function extractClassroomId(profile) {
  return (
    profile?.classroom_id ??
    profile?.classroom?.id ??
    profile?.team?.classroom_id ??
    null
  );
}

function Equipes() {
  const { user, token, saveAuth } = useAuth();

  const [myTeam, setMyTeam]           = useState(null);
  const [allTeams, setAllTeams]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [missingClassroom, setMissingClassroom] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError('');
      setMissingClassroom(false);

      try {
        // Rafraîchit le profil pour avoir les champs les plus récents
        const profileRes = await getProfile(token);
        const freshUser  = profileRes.data;
        saveAuth(freshUser, token);

        const teamId      = freshUser?.team_id ?? user?.team_id;
        const classroomId = extractClassroomId(freshUser) ?? extractClassroomId(user);

        if (!teamId || !classroomId) {
          setMissingClassroom(true);
          setLoading(false);
          return;
        }

        const [allRes, myRes] = await Promise.all([
          getClassTeams(token, classroomId),
          getMyTeam(token, classroomId, teamId),
        ]);

        const sorted = (allRes.data ?? [])
          .slice()
          .sort((a, b) => (b.experience_points ?? 0) - (a.experience_points ?? 0));
        setAllTeams(sorted);
        setMyTeam(myRes.data ?? null);
      } catch (err) {
        setError(err.message || 'Impossible de charger les données des équipes');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const teamId = user?.team_id;

  // Membres de mon équipe, triés par XP si disponible
  const members = myTeam?.members ?? [];
  const hasIndividualXP = members.some((m) => m.experience_points !== undefined);
  const sortedMembers = hasIndividualXP
    ? members.slice().sort((a, b) => (b.experience_points ?? 0) - (a.experience_points ?? 0))
    : members;

  // Rang de mon équipe dans le classement
  const myRank = allTeams.findIndex((t) => t.id === teamId);

  return (
    <div className="equipes-page">

      {/* ── Hero ── */}
      <section className="equipes-hero">
        <div className="equipes-hero-text">
          <p className="equipes-hero-label">Mon équipe</p>
          <h1>{myTeam?.name ?? (loading ? '…' : 'Mon équipe')}</h1>
          {myTeam && (
            <p className="equipes-hero-xp">
              {myTeam.experience_points ?? 0} XP cumulés
            </p>
          )}
        </div>
        <span className="equipes-hero-icon" aria-hidden="true">👥</span>
      </section>

      {error && (
        <div className="equipes-error">
          <p>{error}</p>
          <button onClick={() => setError('')}>Fermer</button>
        </div>
      )}

      {missingClassroom && !loading && (
        <div className="equipes-notice">
          <span>ℹ️</span>
          <p>
            Ton équipe existe, mais le backend ne retourne pas encore le
            <code> classroom_id</code> dans <code>/members/me</code>.
            Consulte la doc back pour l'ajouter.
          </p>
        </div>
      )}

      {loading ? (
        <p className="equipes-loading">Chargement…</p>
      ) : !error && !missingClassroom && (
        <>
          {/* ── Membres ── */}
          <section>
            <h2 className="equipes-section-title">Membres de mon équipe</h2>

            {sortedMembers.length === 0 ? (
              <p className="equipes-empty">Aucun membre dans cette équipe.</p>
            ) : (
              <div className="equipes-members-list">
                {sortedMembers.map((member, index) => {
                  const isMe = member.id === user?.id;
                  const rank = hasIndividualXP ? getRankLabel(index) : null;

                  return (
                    <div
                      key={member.id}
                      className={`member-row${isMe ? ' member-row-me' : ''}`}
                    >
                      {rank && (
                        <span className="member-rank">{rank}</span>
                      )}
                      {!rank && (
                        <div className={`member-avatar${isMe ? ' member-avatar-me' : ''}`}>
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </div>
                      )}
                      {rank && (
                        <div className={`member-avatar${isMe ? ' member-avatar-me' : ''}`}>
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </div>
                      )}

                      <div className="member-info">
                        <span className="member-name">
                          {member.first_name} {member.last_name}
                          {isMe && <span className="member-me-tag">Moi</span>}
                        </span>
                      </div>

                      {hasIndividualXP && (
                        <span className="member-xp">
                          {member.experience_points ?? 0} XP
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── Classement de la classe ── */}
          {allTeams.length > 0 && (
            <section>
              <h2 className="equipes-section-title">Classement de la classe</h2>

              <div className="equipes-ranking-list">
                {allTeams.map((team, index) => {
                  const isMyTeam = team.id === teamId;
                  const medal = getRankLabel(index);

                  return (
                    <div
                      key={team.id}
                      className={`ranking-row${isMyTeam ? ' ranking-row-mine' : ''}`}
                    >
                      <span className="ranking-medal">{medal}</span>

                      <div className="ranking-team-info">
                        <span className="ranking-team-name">
                          {team.name}
                          {isMyTeam && <span className="ranking-mine-tag">Mon équipe</span>}
                        </span>
                        <span className="ranking-members-count">
                          {team.members?.length ?? 0} membre{(team.members?.length ?? 0) > 1 ? 's' : ''}
                        </span>
                      </div>

                      <div className="ranking-xp-col">
                        <span className="ranking-xp-value">
                          {team.experience_points ?? 0}
                        </span>
                        <span className="ranking-xp-label">XP</span>
                      </div>

                      {isMyTeam && myRank === 0 && (
                        <span className="ranking-crown" aria-label="Première place">👑</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default Equipes;
